import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

/**
 * ============================================================
 *  统一 API 配置文件
 * ============================================================
 *
 *  本文件集中管理所有后端接口的基础地址、请求路径和请求方法。
 *  后续对接真实后端时，只需修改此文件中的配置即可：
 *
 *  1. 将 USE_MOCK 改为 false
 *  2. 修改 apiConfig.baseURL 为你的后端接口地址
 *  3. 按需调整 API_ENDPOINTS 中的路径与方法
 *
 * ============================================================
 */

// TODO: 对接真实后端时，将 USE_MOCK 改为 false
export const USE_MOCK = false;

export interface ApiConfig {
  baseURL: string;
  timeout: number;
}

// TODO: 对接真实后端时，修改 baseURL 为你的后端接口地址
const DEFAULT_CONFIG: ApiConfig = {
  baseURL: 'http://sever.xihoo.icu',
  timeout: 15000,
};

export const apiConfig = {
  baseURL: DEFAULT_CONFIG.baseURL,
  setBaseURL(url: string): void {
    this.baseURL = url;
  },
};

/**
 * 接口端点定义 — 所有接口路径与请求方法集中在此处管理
 * TODO: 对接真实后端时，按需调整各接口的 path 和 method
 */
export const API_ENDPOINTS = {
  orders: {
    list: { path: '/api/orders', method: 'GET' },
    getById: { path: '/api/orders/:id', method: 'GET' },
    create: { path: '/api/orders', method: 'POST' },
    update: { path: '/api/orders/:id', method: 'PATCH' },
    remove: { path: '/api/orders/:id', method: 'DELETE' },
    upload: { path: '/api/upload', method: 'POST' },
  },
} as const;

const requestInterceptors: Array<
  (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig
> = [];
const responseInterceptors: Array<(data: unknown) => unknown> = [];
const errorInterceptors: Array<(error: unknown) => unknown> = [];

export function registerRequestInterceptor(
  fn: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig,
): void {
  requestInterceptors.push(fn);
}

export function registerResponseInterceptor(
  fn: (data: unknown) => unknown,
): void {
  responseInterceptors.push(fn);
}

export function registerErrorInterceptor(
  fn: (error: unknown) => unknown,
): void {
  errorInterceptors.push(fn);
}

export function createRequest(
  config: Partial<ApiConfig> = {},
): AxiosInstance {
  const merged: ApiConfig = { ...DEFAULT_CONFIG, ...config };
  const instance = axios.create({
    baseURL: merged.baseURL,
    timeout: merged.timeout,
  });

  instance.interceptors.request.use((cfg) => {
    let result = cfg;
    for (const fn of requestInterceptors) {
      result = fn(result);
    }
    return result;
  });

  instance.interceptors.response.use(
    (response) => {
      let data = response.data;
      for (const fn of responseInterceptors) {
        data = fn(data);
      }
      return { ...response, data };
    },
    (error) => {
      let err = error;
      for (const fn of errorInterceptors) {
        err = fn(err) ?? err;
      }
      return Promise.reject(err);
    },
  );

  return instance;
}

export const request = createRequest();

export type RequestOptions = AxiosRequestConfig;
